import React from 'react'
import moment from 'moment'
import { useHistory } from 'react-router'
import { ButtonBase } from '@mui/material'
import { analytics } from 'Modules/Analytics'
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useAppState } from 'Context/AppContext/AppContext'
import { Goal } from 'Generated/graphql-types'
import { funderGoals } from 'Modules/Analytics/trackActionInterface/FunderPlatform/funderGoals'
import { categoriesIncludingOld } from 'TypeDefs/Goal'
import { colors } from '@resiliatech/birdsong'
import { GoalCompletionChip } from 'Components/Pages/Impact/GoalsDashboard/Chips/GoalCompletionChip'
import { GoalProgress } from '../../Impact/GoalsDashboard/Goals/GoalProgress'
import {
  Category,
  Date as DateLabel,
  Details,
  GoalCardContainer,
  DateInfo,
  GoalDescription,
  GoalTitle,
  Dates,
  TaskStatus,
  TopSection,
  TargetProgress,
  NonprofitNameText,
  LeftChipsWrapper,
  ChipsWrapper,
  TinyDot,
  CategoryContainer,
} from './FunderGoals.styled'
import { GOALS_PATH } from '../../Impact/GoalsDashboard/types'
import { FUNDER_IMPACT_GOALS_PATH } from '../../Impact/GoalPage/utils'
import { getNonprofitName } from './utils'

type GoalSharedToFunderCardProps = {
  goal: Goal
  isInteractive?: boolean
  targetProgress?: number | null
}

const GOAL_DATE_FORMAT = 'MM-DD-YYYY'

const iconColor = { color: '#706666' }

export const GoalSharedToFunderCard = ({
  goal,
  isInteractive = true,
  targetProgress,
}: GoalSharedToFunderCardProps) => {
  const {
    userConfig: { id, email },
    funderOrgConfig: { sponsoredNonprofits },
  } = useAppState()

  const history = useHistory()

  if (!GoalSharedToFunderCard) return null

  const nonprofitName = getNonprofitName(
    sponsoredNonprofits,
    goal.nonprofitOrgId
  )

  const goalCategory =
    goal.category && categoriesIncludingOld[goal.category]
      ? categoriesIncludingOld[goal.category]
      : ''

  const dispatchGoalCardClicked = () => {
    analytics.trackActionCreator(funderGoals.Goal_Shared_With_Funder_Clicked)({
      goalId: goal.id,
      nonprofitOrgId: goal.nonprofitOrgId,
      userId: id,
      userEmail: email,
      goalType: goal.type,
    })
  }

  const handleRouteToGoal = () => {
    const isFunderImpactPath = history.location.pathname.includes(
      FUNDER_IMPACT_GOALS_PATH
    )
    const goalPath = `${GOALS_PATH}/${goal.id}?section=tasks`
    const funderImpactGoalPath = `${FUNDER_IMPACT_GOALS_PATH}/${goal.id}`

    if (!isFunderImpactPath) {
      history.push(goalPath)
    } else {
      history.push(funderImpactGoalPath)
    }
    dispatchGoalCardClicked()
  }

  const totalTasks = goal.ChecklistItem ? goal.ChecklistItem.length : 0
  const completedTasks = goal.ChecklistItem
    ? goal.ChecklistItem.filter((task) => task.isCompleted === true).length
    : 0
  const numTasksIncomplete = totalTasks - (completedTasks || 0)
  const numTasksTotal = totalTasks
  const numTasksComplete = numTasksTotal - numTasksIncomplete
  const taskStatusPercentage = Math.round(
    (numTasksComplete / numTasksTotal) * 100
  )
  const taskStatusIndicator =
    taskStatusPercentage === 100 ? (
      <CheckCircleOutlineOutlinedIcon />
    ) : (
      <GoalProgress value={taskStatusPercentage} showLabel={false} size={20} />
    )
  const hasTasks = numTasksTotal > 0

  const hasTarget = goal.target && goal.target > 0
  const goalTargetProgress = targetProgress || 0
  const targetProgressStatusPercentage = goal.target
    ? Math.min(Math.round((goalTargetProgress / goal.target) * 100), 100)
    : 0
  const targetProgressStatusIndicator =
    targetProgressStatusPercentage === 100 ? (
      <CheckCircleOutlineOutlinedIcon />
    ) : (
      <GoalProgress
        value={targetProgressStatusPercentage}
        showLabel={false}
        size={20}
      />
    )

  const BaseContainer = isInteractive ? ButtonBase : React.Fragment

  const getVariant = (taskStatusPercent: number) => {
    if (taskStatusPercent === 0) {
      return 'warning'
    }
    if (taskStatusPercent === 100) {
      return 'completed'
    }
    return 'informational'
  }

  return (
    <BaseContainer>
      <GoalCardContainer
        onClick={isInteractive ? handleRouteToGoal : undefined}
        isInteractive={isInteractive}
      >
        <Details>
          <TopSection>
            {goalCategory && goalCategory !== 'None' && (
              <CategoryContainer>
                <Category>{goal.impactCategory}</Category>
                <TinyDot color={colors.feedback.infoDark} />
                <Category>{goalCategory}</Category>
              </CategoryContainer>
            )}
            {goal.title && (
              <GoalTitle data-testid='goalTitle' variant='h5'>
                {goal.title}
              </GoalTitle>
            )}
            <Dates>
              <DateInfo>
                <DateLabel variant='caption'>
                  Start Date:{' '}
                  {goal.startDate
                    ? moment.utc(goal.startDate).format(GOAL_DATE_FORMAT)
                    : 'Not Specified'}
                </DateLabel>
                <TinyDot />
                <DateLabel variant='caption'>
                  Due Date:{' '}
                  {goal.endDate
                    ? moment.utc(goal.endDate).format(GOAL_DATE_FORMAT)
                    : 'Not Specified'}
                </DateLabel>
              </DateInfo>
            </Dates>
          </TopSection>
          {goal.description && (
            <GoalDescription variant='body1'>
              {goal.description}
            </GoalDescription>
          )}
          <ChipsWrapper>
            <LeftChipsWrapper>
              {hasTarget && (
                <TargetProgress
                  variant={getVariant(targetProgressStatusPercentage)}
                >
                  {targetProgressStatusPercentage > 0 &&
                    targetProgressStatusIndicator}
                  {targetProgressStatusPercentage}% to Goal Target
                </TargetProgress>
              )}
              {hasTasks && (
                <TaskStatus variant={getVariant(taskStatusPercentage)}>
                  {taskStatusPercentage > 0 && taskStatusIndicator}
                  {taskStatusPercentage}% Tasks Completed
                </TaskStatus>
              )}
              {goal.isComplete && (
                <GoalCompletionChip iconColorStyles={iconColor} />
              )}
            </LeftChipsWrapper>
            <TaskStatus variant='shared'>
              <VisibilityIcon sx={iconColor} />
              From <NonprofitNameText>{nonprofitName}</NonprofitNameText>
            </TaskStatus>
          </ChipsWrapper>
        </Details>
      </GoalCardContainer>
    </BaseContainer>
  )
}
